export const organizationConfiguration = {
  title: 'Organization Management',
  description: 'Manage your organization configuration and tags',
  tabs: {
    tags: 'Tags',
  },
} as const

export const companySettings = {
  title: 'Company Settings',
  description: 'Manage your company information and details',
  tabs: {
    websites: 'Websites',
    competitors: 'Competitors',
    companyData: 'Company Data',
    brandIdentity: 'Brand Identity',
    brandVoices: 'Brand Voices',
    linkedinProfiles: 'LinkedIn Profiles',
    clientProfile: 'Client Profile',
  },
  save: 'Save Changes',
  updateSuccess: 'Company data updated successfully',
  updateError: 'Failed to update company data',
  errorLoading: 'Failed to load company data',
  websites: {
    title: 'Websites',
    description:
      'Manage your company websites for content interlinking. One website per domain or subdomain.',
    addWebsite: 'Add Website',
    editWebsite: 'Edit Website',
    deleteWebsite: 'Delete Website',
    name: 'Name',
    namePlaceholder: 'e.g., Main Blog',
    url: 'Website URL',
    urlPlaceholder: 'e.g., example.com',
    confirmDelete: 'Are you sure you want to delete this website?',
    createSuccess: 'Website created successfully',
    addError: 'Failed to add website. Please enter a valid URL.',
    domainConflict:
      'You already have a website registered for this address. Subdomains count as separate websites.',
    // Never rendered: i18next always resolves the _one/_other forms when count is passed. The base key
    // only exists because I18nKey is derived from this tree, so t('…indexedPages') needs the literal.
    indexedPages: '{{count}} pages indexed',
    indexedPages_one: '{{count}} page indexed',
    indexedPages_other: '{{count}} pages indexed',
    noIndexedPages: 'No pages indexed yet',
    unknownIndexedPages: 'Page count unavailable',
    indexing: 'Indexing…',
    indexingFailed: 'Indexing failed',
    reindex: 'Reindex',
    reindexStarted: 'Reindex started',
    reindexConfirm: {
      title: 'Reindex this website?',
      description: 'Reindexing rebuilds the index from whatever this crawl finds.',
      unknownPageCount: 'We could not check how many pages are indexed right now.',
      pageLossWarning:
        'A page the crawler misses this time is only flagged, but a page missed by two crawls in a row is dropped from the index and stops being available for interlinking — so reindexing repeatedly while coverage is unstable can remove pages that are still live.',
      confirm: 'Reindex',
    },
    updateSuccess: 'Website updated successfully',
    updateError: 'Failed to update website. Please enter a valid URL.',
    deleteSuccess: 'Website deleted successfully',
    noWebsites: 'No websites configured',
    noWebsitesDescription: 'Add websites to enable automatic interlinking in your content',
    crawlConfig: {
      title: 'Advanced crawl configuration',
      description:
        'Control which URLs get indexed for interlinking. One pattern per line. Leave both empty to crawl the whole site. Saving does not start a crawl: changes take effect when you reindex.',
      excludeLabel: 'Exclude URL patterns',
      excludePlaceholder: '/choose-your-number\n/tag',
      excludeHelp:
        'Skip URLs matching any of these. Use it to keep programmatic or low-value pages (e.g. /choose-your-number) out of the index.',
      includeLabel: 'Include URL patterns',
      includePlaceholder: '/blog\n/guides',
      includeHelp:
        'When set, only URLs matching at least one pattern are indexed. Usually leave this empty: excludes are safer, since a strict include can stop the crawler from reaching pages.',
      patternsError: 'Up to {{maxPatterns}} patterns, each at most {{maxLength}} characters.',
    },
  },
  competitors: {
    title: 'Competitors',
    description: "Manage your organization's competitors",
    addCompetitor: 'Add Competitor',
    editCompetitor: 'Edit Competitor',
    deleteCompetitor: 'Delete Competitor',
    name: 'Name',
    namePlaceholder: 'e.g., Competitor Inc.',
    url: 'Website URL',
    urlPlaceholder: 'e.g., competitor.com',
    confirmDelete: 'Are you sure you want to delete this competitor?',
    createSuccess: 'Competitor created successfully',
    addError: 'Failed to add competitor. Please enter a valid URL.',
    updateSuccess: 'Competitor updated successfully',
    updateError: 'Failed to update competitor. Please enter a valid URL.',
    deleteSuccess: 'Competitor deleted successfully',
    noCompetitors: 'No competitors configured',
    noCompetitorsDescription:
      'Add competitors to prevent them from being mentioned in generated content',
  },
  brandTokens: {
    title: 'Brand identity',
    description:
      'Terms that identify your brand in search queries. Press Enter to add each token. Leave empty to auto-derive from your organization name.',
    label: 'Brand tokens',
    placeholder: 'Type a token and press Enter',
    addError: 'Tokens must be 1-60 characters and unique.',
    remove: 'Remove',
    autoDerivedHint: 'No tokens configured — derived automatically from "{{name}}".',
  },
  productExclusions: {
    label: 'What your product does NOT do',
    placeholder:
      'E.g.: we do not issue DIAN electronic invoices, we do not serve enterprise (500+ employees), we do not target the automotive industry.',
  },
  linkedinProfiles: {
    title: 'LinkedIn Profiles',
    description: 'Add LinkedIn profiles to link posts in your content plans',
    addProfile: 'Add Profile',
    deleteProfile: 'Delete Profile',
    url: 'LinkedIn URL',
    urlPlaceholder: 'e.g., linkedin.com/in/johndoe',
    type: 'Type',
    typePersonal: 'Personal',
    typeCompany: 'Company',
    confirmDelete: 'Are you sure you want to delete this LinkedIn profile?',
    createSuccess: 'LinkedIn profile added successfully',
    addError: 'Failed to add profile. Please enter a valid LinkedIn URL.',
    deleteSuccess: 'LinkedIn profile deleted successfully',
    fetchPosts: 'Pull Posts',
    fetchPostsSuccess: '{{count}} LinkedIn posts fetched',
    noProfiles: 'No LinkedIn profiles configured',
    noProfilesDescription: 'Add LinkedIn profiles to automatically link relevant posts in your content plans',
  },
  clientProfile: {
    title: 'Client Profile',
    description:
      'Operational blog-pipeline configuration for this organization. Unset values fall back to the organization or the system defaults.',
    sections: {
      voice: 'Voice & register',
      branding: 'Branding & freshness',
      advanced: 'Publishing & advanced',
    },
    fields: {
      register: {
        label: 'Register',
        help: 'Grammatical address used in generated copy.',
      },
      brandSuffix: {
        label: 'Brand suffix',
        placeholder: 'e.g., | Brand Name',
        help: 'Text appended to the end of titles.',
      },
      emojiRule: {
        label: 'Emojis',
        help: 'Whether emojis are kept or stripped from content.',
      },
      freshnessWindowDays: {
        label: 'Freshness window (days)',
        help: 'Number of days content is considered fresh (1 to 365).',
      },
      yearRefreshMode: {
        label: 'Year refresh mode',
        help: 'How year references in content are refreshed.',
      },
      forbiddenTerms: {
        label: 'Forbidden terms',
        placeholder: 'Type a term and press Enter',
        help: 'Terms that must not appear in generated content.',
      },
      mandatoryCapitalizations: {
        label: 'Mandatory capitalizations',
        placeholder: 'Type a term and press Enter',
        help: 'Terms that must always keep this exact casing.',
      },
      locationCode: {
        label: 'Country',
      },
      languageCode: {
        label: 'Language code',
      },
      market: {
        label: 'Market / Country',
        placeholder: 'Select a country',
        help: 'Target country for keyword research. Sets the market and language.',
      },
      wordpressMetaPlugin: {
        label: 'WordPress meta plugin',
        help: 'SEO plugin used to write metadata into WordPress.',
      },
      brandTerms: {
        label: 'Brand terms',
        placeholder: 'Type a term and press Enter',
        help: 'Terms that identify your brand. Stored at the organization level.',
      },
      valueProp: {
        label: 'Value proposition',
        placeholder: 'e.g., The simplest way to manage your SEO',
        help: 'Brand value proposition. Stored at the organization level.',
      },
      notes: {
        label: 'Notes',
        placeholder: 'Internal notes about this configuration',
      },
      publishRouting: {
        label: 'Publish routing',
        help: 'Defines where content is published (never how to authenticate).',
        method: 'Publish method',
        none: 'No routing',
        authorId: 'Author ID',
        authorIdPlaceholder: 'e.g., 12',
        categoryIdsByLang: 'Category IDs by language',
        categoryLangLabel: 'Language',
        categoryIdsLabel: 'IDs (comma-separated)',
        addCategory: 'Add language',
        framerCollections: 'Framer collections',
        framerMarketLabel: 'Market',
        framerCollectionLabel: 'Collection ID',
        addFramerCollection: 'Add collection',
        removeEntry: 'Remove',
        notes: 'Routing notes',
      },
    },
    options: {
      register: {
        voseo: 'Voseo',
        tuteo: 'Tuteo',
        usted: 'Usted',
        neutro: 'Neutral',
      },
      emojiRule: {
        keep: 'Keep',
        strip: 'Strip',
      },
      yearRefreshMode: {
        ask: 'Ask',
        atomic_all: 'Refresh all',
        atomic_skip: 'Skip',
      },
      wordpressMetaPlugin: {
        rankmath: 'Rank Math',
        yoast: 'Yoast',
        none: 'None',
      },
      publishMethod: {
        'wordpress-apply': 'WordPress',
        'mcp-native': 'MCP native',
        'framer-api': 'Framer API',
      },
      useDefault: 'Use default',
    },
    effectiveConfig: {
      title: 'Effective configuration',
      description:
        'Resulting values after applying precedence: profile over organization over system defaults.',
      sourceLabel: 'Source',
      sources: {
        request: 'Request',
        profile: 'Profile',
        organization: 'Organization',
        default: 'Default',
      },
    },
    actions: {
      save: 'Save Profile',
      saving: 'Saving...',
      clear: 'Clear Profile',
    },
    form: {
      freshnessWindowRange: 'The freshness window must be between 1 and 365 days.',
      locationCodePositive: 'The location code must be a positive integer.',
      languageCodeLength: 'The language code must be 2 to 5 characters.',
      authorIdPositive: 'The author ID must be a positive integer.',
      framerCollectionsRequired:
        'Framer collections are required when the method is Framer API.',
    },
    toast: {
      saveSuccess: 'Client profile saved successfully',
      saveError: 'Failed to save the client profile: {{message}}',
      clearSuccess: 'Client profile cleared successfully',
      clearError: 'Failed to clear the client profile: {{message}}',
    },
    states: {
      loading: 'Loading the client profile...',
      error: 'Failed to load the client profile',
      empty: 'No client profile configured yet',
      emptyDescription:
        'Configure the client profile to tailor the blog pipeline for this organization.',
      readOnly: 'Only organization admins can edit the client profile.',
    },
    tags: {
      remove: 'Remove',
    },
    confirmClear: {
      title: 'Clear the client profile?',
      description:
        'This removes the client profile for this organization and reverts the configuration to the organization or system defaults. This action cannot be undone.',
      confirm: 'Clear Profile',
      cancel: 'Cancel',
    },
    comingSoon: 'The client profile editor will be available soon.',
  },
} as const
